'use client';

import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import LoadingSpinner from '@/app/components/ui/Loader/LoadingSpinner';
import ThreatListingCard from '@/app/components/ui/ThreatBriefings/ThreatListingCard';

// Animation variants for staggered children animation
const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.03, // Slight delay between each child animation
    },
  },
} as const;

// Add ITEMS_PER_PAGE constant
const ITEMS_PER_PAGE = 12;

export default function ThreatListingClientNextUp({
  threatBriefingCollection,
  currentThreatBriefing,
}: {
  threatBriefingCollection: any;
  currentThreatBriefing: any;
}) {
  // Sort blogs by date, excluding the current blog
  const sortedThreatBriefings = threatBriefingCollection
    .filter((threatBriefing: any) => threatBriefing.sys.id !== currentThreatBriefing.sys.id)
    .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const [page, setPage] = useState(1);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // Replace existing useLayoutEffect with this
  useEffect(() => {
    if (inView) {
      setPage(prevPage => prevPage + 1);
    }
  }, [inView]);

  const displayedItems = useMemo(() => {
    return sortedThreatBriefings.slice(0, page * ITEMS_PER_PAGE);
  }, [sortedThreatBriefings, page]);

  const noThreatBriefings = sortedThreatBriefings.length === 0;

  return (
    <>
      <motion.div
        layout
        variants={stagger}
        initial="initial"
        animate="animate"
        className={clsx('row-start-2 grid grid-cols-12 gap-x-8 sm:gap-x-0 sm:gap-y-15', {
          'gap-y-10': noThreatBriefings,
          'gap-y-20': !noThreatBriefings,
        })}
      >
        <AnimatePresence>
          {noThreatBriefings ? (
            <span className="col-span-full">
              <h1 className="text-Body-Large">No other threat briefings found.</h1>
            </span>
          ) : (
            displayedItems
              .slice(0, 50)
              .map((threatBriefing: any, index: number) => (
                <ThreatListingCard key={`${threatBriefing.sys.id}-${index}`} card={threatBriefing} initiallyVisible={index < 3} />
              ))
          )}
        </AnimatePresence>
      </motion.div>
      {displayedItems.length < sortedThreatBriefings.length && (
        <div ref={ref} className="relative col-span-full row-start-3 h-20">
          <LoadingSpinner className="mx-auto h-10 w-10" />
        </div>
      )}
    </>
  );
}
